<template>
  <div class="popup">
    <div class="inner">
      <template v-if="pending">
        <div class="popup-body">
          <div class="loader-container">
            <img class="loader" :src="getGif('loader-schakel.gif')" />
            <span class="title">
              {{ $t('PopupOrder.switch_material_is_being_added') }}
            </span>
          </div>
        </div>
      </template>
      <template v-else-if="state === states.SUCCESS">
        <div class="popup-header c-success">
          <img class="icon" :src="getIcon('checkmark-circle.svg')" />
          <h4>
            {{ $t('PopupOrder.chosen_material_added') }}
          </h4>
        </div>
        <div v-if="notFoundProducts.length" class="popup-content-container">
          <div class="popup-content-header">
            <img class="icon" :src="getIcon('alert-circle-warning.svg')" />
            {{ $t('PopupOrder.not_found_products_message') }}
          </div>
          <div
            v-for="notFoundProduct in notFoundProducts"
            :key="notFoundProduct.id"
            class="popup-content-item"
          >
            <img
              :src="notFoundProduct.image"
              alt="NotFoundProductImage"
              class="popup-content-image"
            />
            {{ notFoundProduct.name }}
          </div>
        </div>
        <div class="popup-footer order">
          <div class="row">
            <a :href="url.shop" class="emat-btn">
              {{ $t('PopupOrder.continue_shopping') }}
            </a>
            <button @click="close" class="emat-btn emat-btn-gray">
              {{ $t('PopupOrder.stay_on_this_page') }}
            </button>
          </div>
          <div class="row">
            <a :href="url.cart" class="emat-btn emat-btn-success">
              {{ $t('PopupOrder.to_cart') }}
            </a>
          </div>
          <PopupOrderEnvironmentLinks
            v-if="$i18n.locale.toLowerCase() !== 'be'"
          />
        </div>
      </template>
      <template v-else-if="state === states.FAILED">
        <div class="popup-header c-warning">
          <img class="icon" :src="getIcon('alert-circle-warning.svg')" />
          <h1>{{ $t('global.error.something_went_wrong') }}</h1>
          <img @click="close" class="times" :src="getIcon('close.svg')" />
        </div>
        <div class="popup-body">
          <p v-if="notFoundProducts.length">
            {{ $t('PopupOrder.not_found_products_message') }}
          </p>
          <p v-else>
            {{ $t('global.error.something_went_wrong_try_again') }}
            <a href="tel: 088 51 11 600">088 51 11 600</a>
          </p>
        </div>
        <div
          v-if="notFoundProducts.length"
          class="popup-content-container failed"
        >
          <div
            v-for="notFoundProduct in notFoundProducts"
            :key="notFoundProduct.id"
            class="popup-content-item"
          >
            <img
              :src="notFoundProduct.image"
              alt="NotFoundProductImage"
              class="popup-content-image"
            />
            {{ notFoundProduct.name }}
          </div>
        </div>
        <div class="popup-footer">
          <button
            v-if="notFoundProducts.length"
            class="emat-btn"
            @click="close"
          >
            {{ $t('global.error.alter') }}
          </button>
          <button v-else class="emat-btn" @click="tryAgain">
            {{ $t('global.error.try_again') }}
          </button>
        </div>
      </template>
    </div>
    <div class="overlay" @click="close"></div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import Axios from 'axios';
import PopupOrderEnvironmentLinks from './PopupOrderEnvironmentLinks';
import { sendToMagentoCart } from '@/services/magentoCart';
import {
  createDataLayerProduct,
  pushCartToDataLayer,
} from '@/services/pushCartToDataLayer';

export default {
  name: 'PopupOrder',

  components: { PopupOrderEnvironmentLinks },

  data() {
    return {
      state: undefined,
      url: {
        add_cart: undefined,
        cart: undefined,
        show: undefined,
      },
      guid: null,
      notFoundProductSkus: [],
    };
  },

  computed: {
    ...mapGetters('order', {
      orders: 'getRoomOrders',
    }),
    ...mapGetters('build', {
      hasRooms: 'hasRooms',
      roomGuids: 'getRoomGuids',
    }),
    ...mapGetters({
      pending: 'getPending',
      api: 'getApiUrl',
    }),
    ...mapState({
      environmentId: (state) => state.environment,
    }),

    states() {
      return {
        UNDEFINED: undefined,
        SUCCESS: 'success',
        FAILED: 'failed',
      };
    },

    getPostData() {
      const products = this.orders.reduce((acc, order) => {
        order.productsWithAmount.forEach(({ id, amount }) => {
          if (acc[id]) {
            acc[id] += amount;

            return acc;
          }

          acc[id] = amount;
        });
        return acc;
      }, {});

      return {
        // Todo: For now we are using the first room line color type id, Build logic will be updated in ELKT-139
        color_type_id: this.orders[0].room.line.id,
        products,
      };
    },

    axiosWrapper() {
      return Axios.create({
        baseURL: `${process.env.VUE_APP_API_URL}/${this.environmentId}/switch/`,
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
          'Access-Control-Allow-Origin': '*',
        },
      });
    },

    notFoundProducts() {
      const products = this.orders.flatMap((order) => order.productsWithAmount);

      return products.filter((product) => {
        return product.eans.some((ean) =>
          this.notFoundProductSkus.includes(ean.sku)
        );
      });
    },
  },

  methods: {
    ...mapMutations({
      setPending: 'setPending',
    }),
    ...mapMutations('popup', ['closePopup']),

    close() {
      this.closePopup();
    },

    tryAgain() {
      this.addCartToDataLayer();

      this.setPending(true);
      this.state = this.states.UNDEFINED;

      this.url.add_cart = undefined;
      this.url.cart = undefined;
      this.url.show = undefined;

      if (this.hasRooms) {
        this.sendRoomBuild();
      } else {
        this.sendNoRoomBuild();
      }
    },

    failed() {
      this.setPending(false);
      this.state = this.states.FAILED;
      this.url.add_cart = undefined;
    },

    success() {
      this.setPending(false);
      this.state = this.states.SUCCESS;
      this.url.add_cart = undefined;
      this.addCartToDataLayer();

      if (this.hasRooms) {
        return;
      }

      this.axiosWrapper.post('/analytic', {
        guid: this.guid,
      });
    },

    async addCartToDataLayer() {
      const products = [];
      for (const order of this.$store.getters['order/getRoomOrders']) {
        const line = order.room.line;

        const brand = this.$store.getters['brand-color-selection/getBrandById'](
          line.brand_id
        );
        const color = this.$store.getters['brand-color-selection/getColorById'](
          line.color_id
        );

        for (const orderProduct of order.productsWithAmount) {
          const name = `${line.name} ${orderProduct.name}`;

          for (const ean of orderProduct.eans) {
            const price = ean.price / 100;

            products.push(
              createDataLayerProduct(
                name,
                ean.sku,
                price.toFixed(2),
                brand.name,
                color.name,
                orderProduct.amount,
                (orderProduct.amount * price).toFixed(2)
              )
            );
          }
        }
      }

      pushCartToDataLayer(products);
    },

    sendNoRoomBuild() {
      this.axiosWrapper
        .post('/build/make', this.getPostData)
        .then(({ data }) => {
          this.guid = data.guid;
          this.getCart();
        })
        .catch(({ response }) => {
          window.console.error('SEND BUILD FAILED', response);
          this.failed();
        });
    },

    getCart() {
      this.axiosWrapper
        .get(`/build/make/${this.guid}`)
        .then(async ({ data }) => {
          await this.handleMagentoRequest(data);
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            this.setNotFoundProducts(error.response.data);
          }

          this.failed();
        });
    },

    sendRoomBuild() {
      this.axiosWrapper
        .get('room/build/make/cart', {
          params: {
            uids: this.roomGuids,
          },
        })
        .then(async ({ data }) => {
          await this.handleMagentoRequest(data);
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            this.setNotFoundProducts(error.response.data);
          }

          this.failed();
        });
    },

    async handleMagentoRequest(data) {
      this.url.cart = this.formatUrl(data.cart_url);
      this.url.shop = this.formatUrl(data.shop_url);

      const magentoCartResponse = await sendToMagentoCart(
        data.products,
        this.url.shop
      );

      switch (magentoCartResponse.status) {
        case 200:
          break;
        case 202:
          this.setNotFoundProducts(magentoCartResponse.data);
          break;
        default:
          throw 'Unknown situation';
      }
      this.success();
    },

    setNotFoundProducts(notFoundProducts) {
      this.notFoundProductSkus = notFoundProducts.map((product) => {
        return product['productSku'];
      });
    },

    formatUrl(url) {
      let fetched = '';

      for (const urlPart of url.split('/')) {
        if (!urlPart) continue;

        if (fetched !== '') {
          fetched += '/';
        }

        fetched += urlPart;

        if (['http:', 'https:'].includes(urlPart)) {
          fetched += '/';
        }
      }

      return fetched;
    },
  },

  created() {
    this.tryAgain();
  },
};
</script>

<style scoped lang="scss">
.loader-container {
  display: flex;
  align-items: center;
  flex-direction: column;
  text-align: center;

  .loader {
    max-width: 250px;
  }

  .title {
    color: #196cae;
    font-family: 'HellixBold', sans-serif !important;
    font-weight: bold !important;
    font-size: 18px !important;
    margin: 24px 0 !important;
  }
}

#switch-gen {
  .times {
    margin-left: auto;
  }

  .popup-content-container {
    padding: 20px;

    &.failed {
      color: $color-warning;
      font-weight: $font-weight-bold;
    }

    .popup-content-header {
      @include header-font;
      color: $color-warning;
      display: flex;

      .icon {
        max-width: 20px;
        margin-right: 8px;
      }
    }

    .popup-content-item {
      display: flex;
      align-items: center;
      padding: 8px;
      border-bottom: 1px solid $color-border-gray;

      .popup-content-image {
        width: 60px;
        margin-right: 8px;
      }
    }
  }
}
</style>
